import { SourcingCurrencyStats } from 'api-clients/monolith';
import clsx from 'clsx';
import { ReactNode, useMemo } from 'react';
import { useIntl } from 'react-intl';

import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Tooltip } from '@/components/ui/tooltip';
import { Typography, TypographyVariant } from '@/components/ui/typography';
import { parseCurrency } from '@/utils/parse-currency';
import { getProgress } from '@/utils/progress';

import { AggregateSpendHoverCard } from '../pages/dashboard/dashboard-table/aggregate-spend-hover-card';

interface CurrencyStatsProps {
  stats: SourcingCurrencyStats;
  onClick?: () => void;
  typographyVariant?: TypographyVariant;
  className?: string;
  showProgress?: boolean;
  separator?: ReactNode | string;
  tooltipContent?: string;
  isHighlighted?: boolean;
  openingSlug?: string;
  isApproximate?: boolean;
}

export function CurrencyStats({
  stats,
  onClick,
  typographyVariant = 'text',
  className,
  showProgress = false,
  separator = '/',
  tooltipContent,
  isHighlighted = false,
  openingSlug,
  isApproximate = false,
}: CurrencyStatsProps) {
  const intl = useIntl();
  const addMessage = intl.formatMessage({ defaultMessage: '+ Add' });
  const { expected, actual } = stats;

  const progress = useMemo(() => {
    const expectedValue = parseCurrency(expected);
    const actualValue = parseCurrency(actual);

    return getProgress(expectedValue, actualValue);
  }, [expected, actual]);

  function StatValue({
    children,
    localClassName,
  }: {
    children: string;
    localClassName?: string;
  }) {
    return (
      <Typography
        variant={typographyVariant}
        className={clsx(className, localClassName)}
      >
        {children}
      </Typography>
    );
  }

  const renderActual = () => {
    if (!actual) return '-';

    if (isHighlighted && openingSlug) {
      return (
        <AggregateSpendHoverCard openingSlug={openingSlug}>
          <StatValue localClassName="!text-fx-blue bg-fx-blue-50 rounded-full px-1.5 py-0.5">
            {actual}
          </StatValue>
        </AggregateSpendHoverCard>
      );
    }

    return isApproximate ? (
      <Tooltip
        content={intl.formatMessage({
          defaultMessage:
            'This spend data is an estimate assuming the budget is spent uniformly on a daily basis',
        })}
      >
        <Typography variant={typographyVariant} className={className}>
          {`~${actual}`}
        </Typography>
      </Tooltip>
    ) : (
      <StatValue>{actual}</StatValue>
    );
  };

  if (!expected && !actual)
    return onClick ? (
      <Button variant="link" size="link" onClick={onClick}>
        {addMessage}
      </Button>
    ) : (
      <StatValue>-</StatValue>
    );

  return (
    <>
      <div className="flex items-center gap-1">
        {renderActual()}

        {typeof separator === 'string' ? (
          <StatValue>{separator}</StatValue>
        ) : (
          separator
        )}

        {onClick ? (
          <Tooltip content={tooltipContent}>
            <Button
              variant="link"
              size="link"
              onClick={onClick}
              className={clsx({
                '!text-large font-medium': typographyVariant === 'large2',
              })}
            >
              {expected ?? addMessage}
            </Button>
          </Tooltip>
        ) : (
          <StatValue>{expected ?? '-'}</StatValue>
        )}
      </div>

      {showProgress && <Progress value={progress} />}
    </>
  );
}
